
import { AppConfig } from '../src/config.js';

export class SuiteBootstrapError extends Error {
    constructor(message, status = null) {
        super(message);
        this.name = 'SuiteBootstrapError';
        this.code = 'suite_bootstrap_failed';
        this.status = status;
    }
}

export function createSuiteBootstrapRequest(accessToken, languageCode = 'en', config = AppConfig) {
    const language = String(languageCode || 'en').trim() || 'en';
    return {
        url: `${config.suiteApiBaseUrl}/${encodeURIComponent(language)}/bootstrap`,
        options: {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({})
        }
    };
}

export default class Auth {
    static user = {
        jwt: null,
        authenticated: false,
        email: null
    };
    static suiteBootstrapPromise = null;
    static suiteBootstrapError = null;
    static runtimeConfig = AppConfig;

    static base64UrlDecode(str) {
        const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const decodedData = atob(base64);
        try {
            return decodeURIComponent(
                decodedData.split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
            );
        } catch (e) {
            console.error("Decoding failed:", e);
            return null;
        }
    }
    
    static parseJwt(token) {
        try {
            const payloadBase64 = token.split('.')[1];
            if (!payloadBase64) {
               throw new Error("Invalid JWT: Payload not found");
            }
            const payloadJson = Auth.base64UrlDecode(payloadBase64);
            return JSON.parse(payloadJson);
        } catch (error) {
            console.error("JWT parsing error:", error);
            return null;
        }
    }

    static isTokenExpired(token) {
        const payload = Auth.parseJwt(token);
        if (!payload || !payload.exp) return true;
        return Date.now() >= payload.exp * 1000;
    }

    static async refreshTokens() {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const authServiceUrl = AppConfig.authServiceApiBaseUrl;
        const response = await fetch(`${authServiceUrl}/en/1/refresh-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ refreshToken })
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        return data.accessToken;
    }

    static async getValidAccessToken() {
        let accessToken = localStorage.getItem('accessToken');
        if (!accessToken || Auth.isTokenExpired(accessToken)) {
            accessToken = await Auth.refreshTokens();
        }
        return accessToken;
    }

    static resetSuiteBootstrap() {
        Auth.suiteBootstrapPromise = null;
        Auth.suiteBootstrapError = null;
    }

    static ensureSuiteBootstrap() {
        if (Auth.suiteBootstrapPromise) {
            return Auth.suiteBootstrapPromise;
        }

        const bootstrapPromise = (async () => {
            const accessToken = await Auth.getValidAccessToken();
            const languageCode = globalThis.Translations?.langCode ||
                globalThis.Routing?.getLanguageCodeFromPathName?.() ||
                'en';
            const request = createSuiteBootstrapRequest(accessToken, languageCode, Auth.runtimeConfig);

            let response;
            try {
                response = await fetch(request.url, request.options);
            } catch (error) {
                throw new SuiteBootstrapError(
                    'We could not connect to Instant Suite to finish setting up your account. Please try again.'
                );
            }

            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            if (!response.ok || payload.success !== true) {
                throw new SuiteBootstrapError(
                    payload.message || 'We could not finish setting up your Instant Suite account. Please try again.',
                    response.status
                );
            }

            return payload;
        })();

        Auth.suiteBootstrapPromise = bootstrapPromise;
        bootstrapPromise.then(
            () => {
                if (Auth.suiteBootstrapPromise === bootstrapPromise) {
                    Auth.suiteBootstrapError = null;
                }
            },
            (error) => {
                if (Auth.suiteBootstrapPromise === bootstrapPromise) {
                    Auth.suiteBootstrapPromise = null;
                    Auth.suiteBootstrapError = error;
                }
            }
        );

        return bootstrapPromise;
    }

    static refreshHeaderUser() {
        const headerLink = document.querySelector('.logo-flex .f12.ml-4');
        if (!headerLink) return;

        headerLink.textContent = Auth.user?.email || '';
        if (Auth.user?.id) {
            headerLink.setAttribute('href', `/admins/${Auth.user.id}/edit`);
        }
    }

    static setAuthenticatedSession(data) {
        Auth.resetSuiteBootstrap();
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        document.body.className = "authenticated";
        Auth.user = data.user || {};
        Auth.user.authenticated = true;
        if (data.accessToken) {
            Auth.user.jwt = data.accessToken;
        }
        Auth.refreshHeaderUser();
    }
    
    static loginOrRedirectWithAuthCookie() {
        // presume the cookie means they are logged in with valid token. we verify later.
        let accessToken = localStorage.getItem('accessToken');
        let pathname = Routing.getPathWithoutLanguageCode();
        let langCode = Routing.getLanguageCodeFromPathName();
        let routeObject = Routing.getRouteObject();
        let authAccess = routeObject?.authAccess || 'protected';
        
        if (accessToken) {
            document.body.className="authenticated";
            let jwt = Auth.parseJwt(accessToken);
            Auth.user = jwt;
            Auth.user.jwt = accessToken;
            Auth.user.authenticated = true;
            Auth.refreshHeaderUser();

            Auth.ensureSuiteBootstrap().catch(() => {
                // Protected requests and the account verification page surface a retryable error.
            });

            if (authAccess === 'public') {
                console.log('Authorized user is on a public route. Redirect to /.');
                return "/" + langCode;
            }
        } else {
            document.body.className="unauthenticated";
            Auth.user.authenticated = false;
            Auth.refreshHeaderUser();
            if (authAccess === 'protected') {
                console.log('Unauthorized user is on a protected route. Redirect to login.', pathname);
                return "/" + langCode + "/login";
            }
        }
    }
}

globalThis.Auth = Auth;
